"use client";

import { motion } from "framer-motion";
import { Layers, EyeOff, UserX, FileWarning } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type ProblemCardData = {
  icon: React.ElementType;
  title: string;
  description: string;
  accent: "red" | "amber" | "orange" | "rose";
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const PROBLEM_CARDS: ProblemCardData[] = [
  {
    icon: Layers,
    title: "Estoque descentralizado",
    description: "Cada loja num sistema diferente, sem visão consolidada da rede.",
    accent: "red",
  },
  {
    icon: EyeOff,
    title: "Financeiro no escuro",
    description: "Sem saber a margem real de cada carro vendido no fechamento.",
    accent: "amber",
  },
  {
    icon: UserX,
    title: "Equipe sem controle",
    description: "Sem saber o que cada vendedor está fazendo em cada loja.",
    accent: "orange",
  },
  {
    icon: FileWarning,
    title: "Documentos perdidos",
    description: "Contratos e fichas espalhados em pastas físicas e WhatsApp.",
    accent: "rose",
  },
] as const;

// ---------------------------------------------------------------------------
// Style maps (Tailwind static strings — não usar template literals dinâmicos)
// ---------------------------------------------------------------------------
const ACCENT_CARD: Record<ProblemCardData["accent"], string> = {
  red:    "border-red-500/25 hover:border-red-500/55 bg-red-500/5",
  amber:  "border-amber-500/25 hover:border-amber-500/55 bg-amber-500/5",
  orange: "border-orange-500/25 hover:border-orange-500/55 bg-orange-500/5",
  rose:   "border-rose-500/25 hover:border-rose-500/55 bg-rose-500/5",
};

const ACCENT_ICON_WRAP: Record<ProblemCardData["accent"], string> = {
  red:    "bg-red-500/10 border-red-500/30 text-red-400",
  amber:  "bg-amber-500/10 border-amber-500/30 text-amber-400",
  orange: "bg-orange-500/10 border-orange-500/30 text-orange-400",
  rose:   "bg-rose-500/10 border-rose-500/30 text-rose-400",
};

const ACCENT_TITLE: Record<ProblemCardData["accent"], string> = {
  red:    "group-hover:text-red-400",
  amber:  "group-hover:text-amber-400",
  orange: "group-hover:text-orange-400",
  rose:   "group-hover:text-rose-400",
};

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
} as const;

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
} as const;

const headerVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
} as const;

// ---------------------------------------------------------------------------
// ProblemCard
// ---------------------------------------------------------------------------
const ProblemCard = ({ card }: { card: ProblemCardData }) => {
  const Icon = card.icon;

  return (
    <motion.article
      variants={cardVariants}
      className={`group relative flex flex-col gap-4 rounded-2xl border p-6 transition-all duration-300 backdrop-blur-sm ${ACCENT_CARD[card.accent]}`}
      aria-label={card.title}
    >
      {/* Corner glow on hover */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        aria-hidden
      >
        <div className="absolute top-0 left-0 w-24 h-24 bg-current opacity-5 rounded-full blur-2xl" />
      </div>

      {/* Icon */}
      <div
        className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 ${ACCENT_ICON_WRAP[card.accent]}`}
      >
        <Icon className="w-5 h-5" aria-hidden />
      </div>

      {/* Text */}
      <div className="flex flex-col gap-1.5">
        <h3
          className={`text-base font-bold text-foreground transition-colors duration-200 ${ACCENT_TITLE[card.accent]}`}
        >
          {card.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {card.description}
        </p>
      </div>
    </motion.article>
  );
};

// ---------------------------------------------------------------------------
// ProblemSection
// ---------------------------------------------------------------------------
export default function ProblemSection() {
  return (
    <section
      id="problemas"
      className="relative z-10 py-24 overflow-hidden"
      aria-labelledby="problem-headline"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[60%] bg-red-500/4 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-14"
        >
          <h2
            id="problem-headline"
            className="text-3xl md:text-5xl font-black tracking-tight text-foreground mb-4"
          >
            Sua rede ainda{" "}
            <span className="text-red-400 drop-shadow-[0_0_18px_rgba(248,113,113,0.4)]">
              funciona assim?
            </span>
          </h2>
          <p className="text-base text-muted-foreground max-w-lg mx-auto">
            A maioria dos gestores de redes convive com esses problemas todo dia.
          </p>
        </motion.div>

        {/* Grid de cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          role="list"
          aria-label="Problemas comuns em redes de concessionárias"
        >
          {PROBLEM_CARDS.map((card) => (
            <div key={card.title} role="listitem">
              <ProblemCard card={card} />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
